import dayjs from 'dayjs';
export class ExpiringLocalStorage {
  private readonly storageKey: string;

  constructor(storageKey: string) {
    this.storageKey = storageKey;
  }

  setWithExpiration(key: string, value: any) {
    const expirationTime = dayjs().format('YYYY-MM-DD');
    const data = {
      value,
      expirationTime,
    };
    localStorage.setItem(`${this.storageKey}_${key}`, JSON.stringify(data));
  }

  get(key: string): any | undefined {
    const item = localStorage.getItem(`${this.storageKey}_${key}`);
    if (item) {
      const data = JSON.parse(item);
      if (data.expirationTime === dayjs().format('YYYY-MM-DD')) {
        return data.value;
      } else {
        this.remove(key);
      }
    }
    return undefined;
  }

  remove(key: string) {
    localStorage.removeItem(`${this.storageKey}_${key}`);
  }
}

export class TimedSessionCacheStorage {
  private readonly storageKey: string;

  constructor(storageKey: string) {
    this.storageKey = storageKey;
  }

  /**
   * Stores a value with optional timestamp metadata.
   * @param key Storage key
   * @param value Serializable value
   * @param withTimestamp Whether to attach a timestamp (default: true)
   */
  set(key: string, value: any, withTimestamp: boolean = true) {
    const data = withTimestamp ? { value, storedAt: Date.now() } : { value };
    sessionStorage.setItem(`${this.storageKey}_${key}`, JSON.stringify(data));
  }

  /**
   * Gets raw value and optional timestamp (manual TTL checks can use this)
   */
  getWithTimestamp(key: string): { value: any; storedAt?: number } | undefined {
    const raw = sessionStorage.getItem(`${this.storageKey}_${key}`);
    if (!raw) return undefined;

    try {
      const { value, storedAt } = JSON.parse(raw);
      return { value, storedAt };
    } catch {
      this.remove(key); // Corrupted JSON
      return undefined;
    }
  }

  /**
   * Gets value only if fresh (requires it to be stored with timestamp)
   * @param ttlMs Time to live in ms
   */
  getIfFresh(key: string, ttlMs: number): any | undefined {
    const cached = this.getWithTimestamp(key);
    if (!cached || cached.storedAt === undefined) return undefined;

    const isFresh = Date.now() - cached.storedAt < ttlMs;
    if (!isFresh) {
      this.remove(key);
      return undefined;
    }

    return cached.value;
  }

  /**
   * Gets the stored value with no TTL checks (also works with non-timestamped cache)
   */
  get(key: string): any | undefined {
    const raw = sessionStorage.getItem(`${this.storageKey}_${key}`);
    if (!raw) return undefined;

    try {
      const parsed = JSON.parse(raw);
      return parsed.value;
    } catch {
      this.remove(key);
      return undefined;
    }
  }

  /**
   * Removes the stored item
   */
  remove(key: string) {
    sessionStorage.removeItem(`${this.storageKey}_${key}`);
  }
}
